/**
 * useTelemetry — typed event emitter that batches POST /api/telemetry/event
 * calls. Up to 50 events per batch, flushed every 1 second or on visibility
 * change. Survives page navigation via beforeunload sendBeacon fallback.
 */

import { useCallback, useEffect, useRef } from 'react';
import { apiPath } from '../../api.js';

export type TelemetryEvent =
  | { kind: 'feature_used'; feature: string; context?: Record<string, unknown> }
  | { kind: 'perf_sample'; fps: number; ws_latency_ms?: number; voice_quality?: string }
  | {
      kind: 'match_lifecycle';
      event: 'created' | 'joined' | 'concede' | 'declared_win';
      match_ulid?: string;
    }
  | { kind: string; [k: string]: unknown };

type QueuedEvent = {
  kind: string;
  payload: Record<string, unknown>;
  emitted_at: number;
  match_ulid?: string;
};

type TelemetryHook = {
  emit: (event: TelemetryEvent, match_ulid?: string) => void;
  flush: () => void;
};

const FLUSH_INTERVAL_MS = 1000;
const MAX_BATCH = 50;

export function useTelemetry(): TelemetryHook {
  const queueRef = useRef<QueuedEvent[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const flushNow = useCallback(() => {
    if (queueRef.current.length === 0) return;
    const events = queueRef.current.splice(0, MAX_BATCH);
    const payload = JSON.stringify({ events });
    // Prefer sendBeacon when the page is unloading — fire-and-forget,
    // browser guarantees delivery.
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(apiPath('/api/telemetry/event'), blob);
      return;
    }
    fetch(apiPath('/api/telemetry/event'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: payload,
      credentials: 'include',
      keepalive: true,
    }).catch(() => {
      // Telemetry is best-effort; swallow errors.
    });
  }, []);

  useEffect(() => {
    timerRef.current = setInterval(flushNow, FLUSH_INTERVAL_MS);
    const onUnload = () => flushNow();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushNow();
    };
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('beforeunload', onUnload);
      document.removeEventListener('visibilitychange', onVisibility);
      flushNow();
    };
  }, [flushNow]);

  const emit = useCallback(
    (event: TelemetryEvent, match_ulid?: string) => {
      const { kind, ...rest } = event;
      const queued: QueuedEvent = {
        kind,
        payload: rest as Record<string, unknown>,
        emitted_at: Date.now(),
        ...(match_ulid !== undefined && { match_ulid }),
      };
      queueRef.current.push(queued);
      if (queueRef.current.length >= MAX_BATCH) flushNow();
    },
    [flushNow],
  );

  return { emit, flush: flushNow };
}
